import { FunctionComponent, SyntheticEvent, useState } from 'react'
import Dialog from '@/components/_custom/Dialog/Dialog.tsx'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader.tsx'
import { useTranslation } from 'react-i18next'
import DialogBody from '@/components/_custom/DialogBody/DialogBody.tsx'
import { EventDay } from '@/types/EventDay.ts'
import styles from './duplicateEventDay.module.scss'
import CalendarMonth from '@mui/icons-material/CalendarMonth'
import { format, parse } from 'date-fns'
import { getLocalFromLanguage } from '@/modules/helper/languageHelper.ts'
import Comment from '@mui/icons-material/Comment'
import Groups from '@mui/icons-material/Groups'
import { Section } from '@/types/Section.ts'
import Face from '@mui/icons-material/Face'
import ChildCareOutlined from '@mui/icons-material/ChildCareOutlined'
import { Daycare } from '@/types/Daycare.ts'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon.tsx'
import { Check, Close, EventBusy, LockClock } from '@mui/icons-material'
import {
    Autocomplete,
    AutocompleteRenderGetTagProps,
    AutocompleteRenderInputParams,
    Chip,
    TextField
} from '@mui/material'
import moment from 'moment/moment'
import SelectMultipleDateDialogComponent from '../../planning/Dialog/occasionalSlot/occasionalSlotForm/components/SelectMultipleDateDialog/selectMultipleDateDialog'
import { useMutation } from '@apollo/client'
import { DUPLICATE_EVENT_DAY } from '@/modules/API/daycare/eventDayHandler.ts'
import { useNotification } from '@/modules/context/NotificationContext.tsx'
import { useDialog } from '@/hooks/useDialog/useDialog'
import { DAY_FORMAT } from '@/container/planning/Dialog/occasionalSlot/models/constants'

interface DuplicateEventDayDialogProps {
    open: boolean
    handleClose: () => void
    event: EventDay
    daycare: Daycare | undefined
    refetchAllEvents: () => Promise<void>
}

const DuplicateEventDayDialog: FunctionComponent<DuplicateEventDayDialogProps> = ({
    open,
    handleClose,
    daycare,
    event,
    refetchAllEvents
}: DuplicateEventDayDialogProps) => {
    const { t } = useTranslation()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [dateList, setDateList] = useState<string[]>([])

    function handleDateRemove(dateToRemove: string): void {
        setDateList(dateList.filter((date: string): boolean => date !== dateToRemove))
    }

    const [duplicateEventDay] = useMutation(DUPLICATE_EVENT_DAY, {
        variables: {
            daycareId: daycare?.id,
            eventId: event.id,
            dateList: dateList
        },
        onCompleted: () => {
            refetchAllEvents()
            showNotificationSuccess(t('eventDayDuplicated'))
            handleClose()
        },
        onError: () => {
            showNotificationError(t('eventDayNotDuplicated'))
        }
    })

    const { dialog: SelectMultipleDateDialog, openDialog: openSelectMultipleDateDialog } = useDialog({
        Component: SelectMultipleDateDialogComponent,
        props: () => ({
            daycare,
            dateList,
            setDateList,
            startDate: moment().format(DAY_FORMAT),
            closingDays: []
        }),
        dialogTitle: false
    })

    return (
        <Dialog open={open} handleClose={handleClose}>
            <DialogHeader title={t('duplicateEventDay')} />
            <DialogBody className={styles.DialogBody}>
                <div className={styles.TitleContainer}>
                    <CalendarMonth className={styles.IconDaycare} />
                    <div className={styles.TextContainer}>
                        <p className={styles.Title}>{event.title}</p>
                        <p className={styles.Date}>
                            {format(parse(event.date, 'dd-MM-yyyy', new Date()), 'EEEE dd MMMM yyyy', {
                                locale: getLocalFromLanguage()
                            }) +
                                ' ' +
                                t('from').toLowerCase() +
                                ' ' +
                                format(parse(event.startHour, 'HH:mm:ss', new Date()), 'HH:mm') +
                                ' ' +
                                t('to').toLowerCase() +
                                ' ' +
                                format(parse(event.endHour, 'HH:mm:ss', new Date()), 'HH:mm')}
                        </p>
                    </div>
                </div>
                {(event.message || event.sections.length > 0 || event.reservation) && (
                    <div className={styles.BreakLine}></div>
                )}
                {event.message && (
                    <div className={styles.Container}>
                        <Comment className={styles.IconDaycare} />
                        <p className={styles.Text}>{event.message}</p>
                    </div>
                )}

                <div className={styles.Container}>
                    <Groups className={styles.IconDaycare} />
                    <p className={styles.Text}>
                        {event.sections.length > 0 && daycare && daycare.sections
                            ? event.sections
                                  .map(
                                      (section: number): string | undefined =>
                                          daycare.sections?.find((s: Section): boolean => s.id === section)?.name
                                  )
                                  .join(', ')
                            : t('noSection')}
                    </p>
                </div>
                {event.parentOnly && (
                    <div className={styles.Container}>
                        <Face className={styles.IconDaycare} />
                        <p className={styles.Text}> {t('parentOnly')}</p>
                    </div>
                )}
                {event.releaseDate && (
                    <div className={styles.Container}>
                        <LockClock className={styles.IconDaycare} />
                        <p className={styles.Text}>
                            {format(
                                parse(event.releaseDate, 'dd-MM-yyyy HH:mm:ss', new Date()),
                                'EEEE dd MMMM yyyy HH:mm',
                                {
                                    locale: getLocalFromLanguage()
                                }
                            )}
                        </p>
                    </div>
                )}
                {event.reservation && (
                    <>
                        <div className={styles.BreakLine}></div>
                        <div className={styles.Container}>
                            <Face className={styles.IconDaycare} />
                            <p className={styles.Text}>
                                <span className={styles.Bold}>{event.attendant}</span> {t('adults').toLowerCase()}
                            </p>
                        </div>
                        {!event.parentOnly && (
                            <div className={styles.Container}>
                                <ChildCareOutlined className={styles.IconDaycare} />
                                <p className={styles.Text}>
                                    <span className={styles.Bold}>{event.places}</span> {t('childs').toLowerCase()}
                                </p>
                            </div>
                        )}
                        {event.expiryDate && (
                            <div className={styles.Container}>
                                <EventBusy className={styles.IconDaycare} />
                                <p className={styles.Text}>
                                    {format(
                                        parse(event.expiryDate, 'dd-MM-yyyy HH:mm:ss', new Date()),
                                        'EEEE dd MMMM yyyy HH:mm',
                                        {
                                            locale: getLocalFromLanguage()
                                        }
                                    )}
                                </p>
                            </div>
                        )}
                    </>
                )}
                <div className={styles.BreakLine}></div>
                <div className={styles.AutocompleteContainer}>
                    <p className={styles.Text}>{t('areYouSureDuplicate')}</p>
                    <Autocomplete
                        multiple
                        options={dateList}
                        limitTags={3}
                        value={dateList}
                        onChange={(_event: SyntheticEvent, newValue: string[]) => setDateList(newValue)}
                        renderTags={(value: string[], getTagProps: AutocompleteRenderGetTagProps) =>
                            value.map((option: string, index: number) => (
                                <div key={index}>
                                    <Chip
                                        label={option}
                                        {...getTagProps({ index })}
                                        onDelete={() => handleDateRemove(option)}
                                    />
                                </div>
                            ))
                        }
                        renderInput={(params: AutocompleteRenderInputParams) => (
                            <TextField {...params} variant='outlined' rows={1} multiline={true} label={t('dateList')} />
                        )}
                        className={styles.Autocomplete}
                        onOpen={openSelectMultipleDateDialog}
                    />
                </div>
                <div className={styles.ButtonContainer}>
                    <ButtonTextIcon
                        type={'custom'}
                        customIcon={<Check />}
                        customText={t('yes')}
                        onClick={duplicateEventDay}
                        className={styles.Button}
                        disabled={dateList.length === 0}
                    />
                    <ButtonTextIcon
                        type={'custom'}
                        customIcon={<Close />}
                        customText={t('no')}
                        onClick={handleClose}
                        className={styles.Button}
                    />
                </div>
            </DialogBody>
            {SelectMultipleDateDialog}
        </Dialog>
    )
}

export default DuplicateEventDayDialog
